import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { primeAudio } from "../lib/aria";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { ShieldAlert, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login, completeTwoFactor } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // 2FA challenge state
  const [stage, setStage] = useState("password"); // 'password' | 'twofactor'
  const [challengeToken, setChallengeToken] = useState("");
  const [code, setCode] = useState("");
  // Captcha state
  const [captcha, setCaptcha] = useState(null); // {question, captcha_token, ttl_seconds}
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  // Backoff state
  const [backoffUntil, setBackoffUntil] = useState(0);

  const remainingBackoff = Math.max(0, Math.ceil((backoffUntil - Date.now()) / 1000));

  const loadCaptcha = async () => {
    try {
      const r = await api.get("/security/captcha");
      setCaptcha(r.data);
      setCaptchaAnswer("");
    } catch (err) {
      toast.error("Could not load security challenge");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (remainingBackoff > 0) {
      toast.error(`Wait ${remainingBackoff}s before trying again`);
      return;
    }
    setLoading(true);
    try {
      const result = await login(email, password, {
        captcha_token: captcha?.captcha_token,
        captcha_answer: captchaAnswer || undefined,
      });
      if (result?.requires_2fa) {
        setChallengeToken(result.challenge_token);
        setStage("twofactor");
        toast.message("Enter the 6-digit code from your authenticator app");
        return;
      }
      // clear captcha on success
      setCaptcha(null);
      setCaptchaAnswer("");
      if (result?.requires_enrollment) {
        toast.warning("Two-factor authentication is now required. Set it up to continue.");
        nav("/enroll-2fa", { replace: true });
        return;
      }
      toast.success("Welcome back");
      nav(result?.is_admin ? "/admin/reviews" : "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Structured error path (captcha/backoff/locked)
      if (detail && typeof detail === "object") {
        if (detail.code === "captcha_required") {
          await loadCaptcha();
          toast.warning(detail.message || "Solve the security challenge to continue");
        } else if (detail.code === "invalid_credentials") {
          if (detail.captcha_required) await loadCaptcha();
          if (detail.backoff_seconds) {
            setBackoffUntil(Date.now() + detail.backoff_seconds * 1000);
          }
          toast.error(detail.message || "Invalid email or password");
        } else {
          toast.error(detail.message || JSON.stringify(detail));
        }
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitTwoFactor = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user, used_recovery, recovery_remaining } = await completeTwoFactor(challengeToken, code.trim());
      if (used_recovery) {
        toast.warning(`Logged in with recovery code (${recovery_remaining} left). Regenerate codes in Security settings.`);
      } else {
        toast.success("Welcome back");
      }
      nav(user?.is_admin ? "/admin/reviews" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--pa-bg)" }}>
      <header className="px-6 lg:px-12 py-6 shrink-0">
        <Link to="/" className="flex items-baseline gap-2" data-testid="login-brand">
          <span className="font-serif text-2xl text-[var(--pa-text)]">Prostho</span>
          <span className="font-serif text-2xl text-[var(--pa-gold)]">AI</span>
        </Link>
      </header>
      <main className="flex-1 w-full px-6 py-8 sm:py-16">
        <div className="w-full max-w-md mx-auto">
          <div className="kicker mb-3">Sign In</div>
          <h1 className="font-serif text-4xl text-[var(--pa-text)]">
            {stage === "twofactor" ? "Two-factor verification" : "Welcome back"}
          </h1>
          <p className="mt-3 text-sm text-[var(--pa-text-muted)]">
            {stage === "twofactor"
              ? "Open your authenticator app and enter the 6-digit code. Lost your device? Use one of your recovery codes."
              : "Sign in to your prosthodontic clinical workspace."}
          </p>

          {stage === "password" && (
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
              <div>
                <label className="kicker block mb-2">Email</label>
                <input
                  type="email"
                  required
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] focus:border-[var(--pa-gold)] focus:outline-none p-3"
                />
              </div>
              <div>
                <label className="kicker block mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] focus:border-[var(--pa-gold)] focus:outline-none p-3 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    data-testid="login-password-toggle"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] focus:text-[var(--pa-gold)] focus:outline-none p-1 rounded"
                  >
                    {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                  </button>
                </div>
              </div>

              {captcha && (
                <div className="space-y-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/30" data-testid="captcha-block">
                  <div className="flex items-center gap-2 text-xs text-amber-300">
                    <ShieldAlert size={14} />
                    <span>Security challenge required after repeated failed attempts</span>
                  </div>
                  <label className="kicker block">{captcha.question}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    autoFocus
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    data-testid="login-captcha-answer"
                    placeholder="Answer"
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
                  />
                </div>
              )}

              {remainingBackoff > 0 && (
                <div className="text-xs text-amber-400" data-testid="login-backoff">
                  Please wait {remainingBackoff}s before trying again.
                </div>
              )}

              <button
                type="submit"
                disabled={loading || remainingBackoff > 0}
                onPointerDown={primeAudio}
                data-testid="login-submit"
                className="w-full bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-6 py-3 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50"
              >
                {loading ? "Signing in…" : remainingBackoff > 0 ? `Wait ${remainingBackoff}s` : "Sign in"}
              </button>
            </form>
          )}

          {stage === "twofactor" && (
            <form onSubmit={submitTwoFactor} className="mt-10 space-y-5" data-testid="login-2fa-form">
              <div>
                <label className="kicker block mb-2">6-digit code or recovery code</label>
                <input
                  type="text"
                  required
                  autoFocus
                  inputMode="text"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  data-testid="login-2fa-code"
                  placeholder="123456"
                  className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] focus:border-[var(--pa-gold)] focus:outline-none p-3 font-mono tracking-widest text-center text-xl"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !code.trim()}
                onPointerDown={primeAudio}
                data-testid="login-2fa-submit"
                className="w-full bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-6 py-3 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50"
              >
                {loading ? "Verifying…" : "Verify and sign in"}
              </button>
              <button
                type="button"
                onClick={() => { setStage("password"); setCode(""); setChallengeToken(""); }}
                className="block w-full text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)]"
                data-testid="login-2fa-cancel"
              >
                ← Back to email and password
              </button>
            </form>
          )}

          {stage === "password" && (
            <p className="mt-6 text-sm text-[var(--pa-text-muted)]">
              New here?{" "}
              <Link to="/signup" className="text-[var(--pa-gold)] hover:underline" data-testid="link-to-signup">
                Create an account
              </Link>
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
